import { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import configuration from '../../../../../../../configuration'
import { useMobileAppAccess } from '../../../../../../../context/MobileApp'
import { CardPartProps } from '../../../../../../../pages/OnDemandWorkouts/OnDemandWorkouts.d'
import { Workout } from '../../../../../../../redux/features/OnDemand/onDemandWorkouts'
import { formatMeridianWithLocalTZ } from '../../../../../../utility/dates'

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }
}

type UseVirtual = Pick<
  CardPartProps,
  | 'user'
  | 'hasJoinedWorkout'
  | 'isCheckinLoading'
  | 'checkedInWorkout'
  | 'isLoggedIn'
  | 'token'
  | 'onCheckInClick'
  | 'onJoinLiveClick'
  | 'workout'
> & {
  checkinError?: string
  onAutomaticCheckIn?: (workoutId: number) => void
}

const { cgWebsiteRegisterUrl, workoutAnticipationMinutes } = configuration

const getCalendarTitle = (workout: Workout) => {
  const trainerName = workout.trainer
    ? `${workout.trainer.firstName} ${workout.trainer.lastName}`.trim()
    : ''

  return workout.title || `${trainerName} Live Workout` || 'CG Live Workout'
}

const useVirtual = ({
  user,
  hasJoinedWorkout,
  isCheckinLoading,
  checkedInWorkout,
  isLoggedIn,
  token,
  workout,
  onCheckInClick,
  onJoinLiveClick = () => {},
  checkinError,
  onAutomaticCheckIn,
}: UseVirtual) => {
  const [checkingInWorkoutId, setCheckingInWorkoutId] = useState<number>(0)
  const zoomWindowRef = useRef<Window | null>(null)
  const automaticCheckInAttemptedRef = useRef<boolean>(false)
  const [isAddedToCalendar, setIsAddedToCalendar] = useState<boolean>(false)
  const [isAddingToCalendar, setIsAddingToCalendar] = useState<boolean>(false)

  const { isLive, link } = workout
  const workoutStartDate = moment(workout.startTime).local().toDate()
  const workoutStartHour = formatMeridianWithLocalTZ(workoutStartDate)
  const joinLiveButtonText = hasJoinedWorkout ? 're-join live' : 'join live'
  const isCheckingIn = isCheckinLoading && checkingInWorkoutId === workout.id
  const hasCheckedIn = checkedInWorkout && checkedInWorkout.includes(workout.id)
  const { hasMobileAppAccess } = useMobileAppAccess()

  const getWorkoutEndDate = () => {
    if (!workout.startTime) return null

    const start = new Date(workout.startTime)
    return new Date(start.getTime() + workout.duration * 60 * 1000)
  }

  const formatGoogleCalendarDate = (date: Date) =>
    date
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '')

  const openGoogleCalendar = () => {
    if (!workout.startTime) return

    const startDate = new Date(workout.startTime)
    const endDate = getWorkoutEndDate()

    if (!endDate) return

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: getCalendarTitle(workout),
      dates: `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(
        endDate,
      )}`,
      details: workout.description ?? '',
      location: workout.link ?? '',
    })

    window.open(
      `https://calendar.google.com/calendar/render?${params.toString()}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const handleAddToCalendarClick = () => {
    if (!workout.startTime) return

    const endDate = getWorkoutEndDate()

    if (!endDate) return

    if (hasMobileAppAccess) {
      setIsAddingToCalendar(true)

      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: 'ADD_WORKOUT_TO_CALENDAR',
          payload: {
            workoutId: workout.id,
            title: getCalendarTitle(workout),
            startDate: workout.startTime,
            endDate: endDate.toISOString(),
            location: workout.link,
            notes: workout.description,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      )

      return
    }

    openGoogleCalendar()
  }

  const isCheckInWindowOpen = () => {
    if (!workout.startTime || !workout.duration) return false

    const now = moment.utc()
    const checkInStartTime = moment
      .utc(workout.startTime)
      .subtract(workoutAnticipationMinutes, 'minutes')
    const workoutEndTime = moment
      .utc(workout.startTime)
      .add(workout.duration, 'minutes')

    return now.isBetween(checkInStartTime, workoutEndTime, null, '[]')
  }

  const canCheckIn = isCheckInWindowOpen() && isLive

  const shouldExcludeError =
    checkinError &&
    (checkinError.toLowerCase().includes('already checked in') ||
      checkinError.toLowerCase().includes('check-in limit') ||
      checkinError.toLowerCase().includes('limit reached'))
  const showManualCheckIn =
    !!checkinError && !hasCheckedIn && canCheckIn && !shouldExcludeError

  const checkInButtonText = isCheckingIn
    ? 'checking in ...'
    : hasCheckedIn
    ? 'checked in'
    : 'check in'
  const userLoggedInWithVirtualAccess = isLoggedIn && user?.hasOnlineAccess
  const userLoggedInWithNoVirtualAccess = isLoggedIn && !user?.hasOnlineAccess
  const shouldShowWorkoutLink = isLive && userLoggedInWithVirtualAccess
  const targetBasedOnIsWorkoutLive = shouldShowWorkoutLink ? '_blank' : '_self'
  const workoutLinkIfIsLive = shouldShowWorkoutLink ? link : ''

  const onWorkoutClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    shouldShowWorkoutLink &&
      onJoinLiveClick({ target: targetBasedOnIsWorkoutLive, workout })
  }

  const handleCheckinClick = async () => {
    if (!canCheckIn) return

    setCheckingInWorkoutId(workout.id)
    await onCheckInClick?.(user?.id!, workout.id, token!)
    setCheckingInWorkoutId(0)
  }

  const handleRegisterClick = () => {
    window.open(cgWebsiteRegisterUrl, '_blank')
  }

  const handleJoinLiveClick = async () => {
    const zoomWindow = window.open(workout?.link, targetBasedOnIsWorkoutLive)
    zoomWindowRef.current = zoomWindow

    if (zoomWindow && workout.id) {
      sessionStorage.setItem(`pendingCheckIn_${workout.id}`, 'true')
      automaticCheckInAttemptedRef.current = false
    }

    if (!hasCheckedIn && canCheckIn && workout.id && onAutomaticCheckIn) {
      setTimeout(() => {
        if (!automaticCheckInAttemptedRef.current) {
          automaticCheckInAttemptedRef.current = true
          onAutomaticCheckIn(workout.id)
        }
      }, 2000)
    }

    onJoinLiveClick({
      target: targetBasedOnIsWorkoutLive,
      workout,
    })
  }

  const handleFocus = useCallback(() => {
    const pendingCheckIn = sessionStorage.getItem(
      `pendingCheckIn_${workout.id}`,
    )

    if (
      pendingCheckIn === 'true' &&
      !hasCheckedIn &&
      canCheckIn &&
      workout.id
    ) {
      if (!automaticCheckInAttemptedRef.current && onAutomaticCheckIn) {
        automaticCheckInAttemptedRef.current = true
        onAutomaticCheckIn(workout.id)
      }
      sessionStorage.removeItem(`pendingCheckIn_${workout.id}`)
    }
  }, [workout.id, hasCheckedIn, canCheckIn, onAutomaticCheckIn])

  useEffect(() => {
    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [handleFocus])

  useEffect(() => {
    return () => {
      if (zoomWindowRef.current) {
        zoomWindowRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data)

        if (message.workoutId !== workout.id) return

        if (message.type === 'ADD_WORKOUT_TO_CALENDAR_SUCCESS') {
          setIsAddingToCalendar(false)
          setIsAddedToCalendar(true)
        }

        if (message.type === 'ADD_WORKOUT_TO_CALENDAR_ERROR') {
          setIsAddingToCalendar(false)
        }
      } catch {}
    }

    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
  }, [workout.id])

  return {
    workoutLinkIfIsLive,
    onWorkoutClick,
    targetBasedOnIsWorkoutLive,
    workoutStartHour,
    isLive,
    userLoggedInWithVirtualAccess,
    handleCheckinClick,
    hasCheckedIn,
    checkInButtonText,
    joinLiveButtonText,
    userLoggedInWithNoVirtualAccess,
    handleRegisterClick,
    workoutStartDate,
    handleJoinLiveClick,
    showManualCheckIn,
    canCheckIn,
    isAddedToCalendar,
    handleAddToCalendarClick,
    hasMobileAppAccess,
    isAddingToCalendar,
  }
}

export default useVirtual
